import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { redirectCreateSchema, type RedirectView } from '@agency/contracts';
import { Button, Drawer, Notice, SelectField, TextAreaField, TextField, useToast } from '@agency/ui';
import { post } from '../../lib/api.ts';
import { errText, issueMap, orUndef, serverErrors, type Errors } from '../settings/common.tsx';

export type RedirectDraft = { source: string; destination: string; reason?: string; from404?: boolean };

export function RedirectDrawer({ draft, onClose }: { draft: RedirectDraft | null; onClose: () => void }) {
  return (
    <Drawer open={!!draft} onClose={onClose} title="New redirect" footer={null}>
      {draft ? <RedirectForm draft={draft} onClose={onClose} /> : null}
    </Drawer>
  );
}

function RedirectForm({ draft, onClose }: { draft: RedirectDraft; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [source, setSource] = useState(draft.source);
  const [destination, setDestination] = useState(draft.destination);
  const [statusCode, setStatusCode] = useState('301');
  const [reason, setReason] = useState(draft.reason ?? '');
  const [errors, setErrors] = useState<Errors>({});
  const [error, setError] = useState<string | null>(null);
  const gone = statusCode === '410';

  const save = useMutation({
    mutationFn: (body: unknown) => post<RedirectView>('/admin/seo/redirects', body),
    onSuccess: async (r) => {
      await qc.invalidateQueries({ queryKey: ['seo'] });
      toast({ tone: 'success', message: `Redirect created: ${r.source} ${r.destination ? `to ${r.destination}` : 'now returns 410 Gone'}.` });
      onClose();
    },
    onError: (e) => { setErrors(serverErrors(e)); setError(errText(e)); },
  });

  function submit() {
    setError(null);
    const p = redirectCreateSchema.safeParse({ source, destination: gone ? undefined : orUndef(destination), statusCode: Number(statusCode), reason: orUndef(reason) });
    const found: Errors = p.success ? {} : issueMap(p.error.issues);
    if (!gone && !destination.trim()) found.destination = 'Enter where visitors should go.';
    setErrors(found);
    if (Object.keys(found).length || !p.success) return;
    save.mutate(p.data);
  }

  return (
    <form noValidate className="formgrid" onSubmit={(e) => { e.preventDefault(); submit(); }}>
      {error ? <Notice tone="error" title="Could not create the redirect">{error}</Notice> : null}
      {draft.from404 ? <Notice tone="info" title="From the 404 list" live={false}>Source and destination are pre-filled from what visitors requested{draft.destination ? ' and the closest live page' : ''}. Check them before saving.</Notice> : null}
      <TextField data-autofocus label="Source path" required value={source} onChange={(e) => setSource(e.target.value)} hint="The old address, starting with /, for example /en/old-page. A redirect from the same source replaces the existing one." error={errors.source} />
      <SelectField label="Response" value={statusCode} onChange={(e) => setStatusCode(e.target.value)} error={errors.statusCode} hint={gone ? 'Tells search engines the page is permanently removed.' : statusCode === '302' ? 'Temporary: search engines keep the source indexed.' : 'Permanent: search engines move to the destination.'}>
        <option value="301">301 Permanent redirect</option>
        <option value="302">302 Temporary redirect</option>
        <option value="410">410 Gone (no destination)</option>
      </SelectField>
      <TextField label="Destination" required={!gone} disabled={gone} value={gone ? '' : destination} onChange={(e) => setDestination(e.target.value)} hint={gone ? 'Not used for 410 Gone.' : 'A /path on this site or a full https:// address. Loops are rejected; chains are shortened to the final destination.'} error={errors.destination} />
      <TextAreaField label="Reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} hint="Optional note for the next person, for example “Service renamed”." error={errors.reason} />
      <div className="actions__row">
        <Button type="submit" variant="primary" loading={save.isPending}>Create redirect</Button>
        <Button onClick={onClose}>Cancel</Button>
      </div>
    </form>
  );
}
